import React, { useState } from 'react'
import styled from 'styled-components'
import OutsideClickHandler from 'react-outside-click-handler'
import { DropdownItem } from './DropdownItem'
import { SCREEN_SIZE, STYLES } from '../../static/style'
import { DropdownMenu } from './DropdownMenu'
import { DropdownCaretDownIcon } from '../Icon/DropdownCaretDownIcon'
import { FormFieldLabel } from '../FormFieldLabel'
import { Overlay } from '../Overlay'
import { is } from '../../lib/ramda'
import { IDropdownItemProps } from './Dropdown.types'

const StyledDropdown = styled.div`
  margin: ${STYLES.FORM_FIELD_MARGIN};
  background-color: ${STYLES.COLOR_WHITE};
  border-radius: ${STYLES.BORDER_RADIUS};

  ${({ isInline }: { isInline: boolean }) =>
    isInline
      ? `
    display: flex;
    align-items: center;
    margin: 0 1rem 0 0;
    ${SCREEN_SIZE.SM} {
      position: relative;
    }
  `
      : `
    position: relative;
  `}
`

const DropdownContainer = styled.div`
  position: relative;
`

const DisplayItem = styled.div`
  padding: ${STYLES.FIELD_PADDING};
  cursor: pointer;
  display: flex;
  align-items: center;
  font-size: ${STYLES.FONT_SIZE_MD};

  ${({ isShowBorder }) =>
    isShowBorder &&
    `
    border: 1px solid ${STYLES.FIELD_BORDER_COLOR};
    border-radius: ${STYLES.FIELD_BORDER_RADIUS};
    &:hover {
      background-color: ${STYLES.COLOR_ATHENS_GRAY};
    }
  `}
  ${({ isActive }) =>
    isActive &&
    `
    color: ${STYLES.TEXT_COLOR};
    font-weight: ${STYLES.FONT_WEIGHT_BOLD};
  `}
  ${({ isDisabled }) =>
    isDisabled &&
    `
    background-color: ${STYLES.COLOR_MERCURY_GRAY} !important;
    cursor: default !important;
  `}
`

const StyledDropdownCaretDownIcon = styled(DropdownCaretDownIcon)`
  margin-left: 0.5rem;
`

const DisplayText = styled.div`
  flex: 1;
  text-align: left;
  white-space: normal;
  line-height: 1.15rem;
`

const StyledOverlay = styled(Overlay)`
  position: absolute;
  width: 100%;
  height: 100vh;
  top: initial;
  bottom: initial;
  z-index: 1;
  display: block;
  background-color: transparent;
`

const StyledDropdownMenu = styled(DropdownMenu)`
  max-height: 50vh;
  overflow-y: auto;
  ${({ isPositionDown }) =>
    !isPositionDown &&
    `
    position: absolute;
    transform: translateY(-100%);
    top: 0;
  `}
  ${({ isInline, isLeft }) =>
    isInline &&
    `
    border-radius: 0;
    ${SCREEN_SIZE.SM} {
      ${isLeft === true ? 'left: 0;' : 'right: 0;'}
      border-radius: ${STYLES.BORDER_RADIUS};
    }
  `};
`

export interface IDropdownProps {
  className?: string
  isMulti?: boolean
  isMenuLeft?: boolean
  value?: string | Array<string>
  items?: Array<IDropdownItemProps>
  labelText?: string
  validationMessage?: string
  onChange?: (s: string | Array<string>) => void
  isShowDefault?: boolean
  isInline?: boolean
  isShowBorder?: boolean
  required?: boolean
  isDisabled?: boolean
  isMenuPositionDown?: boolean
  name?: string
}

const Dropdown: React.FC<IDropdownProps> = ({
  items,
  value = null,
  isMenuLeft,
  labelText,
  isMulti,
  validationMessage,
  onChange,
  isShowDefault = false,
  isInline = false,
  isShowBorder = true,
  required = false,
  className,
  isDisabled = false,
  isMenuPositionDown = true,
}) => {
  const [isShowMenu, setIsShowMenu] = useState(false)
  const selectedItems = items.filter((item) => {
    const itemValue = item.value === undefined ? item.text : item.value
    if (value === null) {
      return false
    }
    if (is(Array, value)) {
      return value.find((v) => v === itemValue)
    }
    return itemValue === value
  })

  const displayText = selectedItems.map((i) => i.text).join(', ')

  const closeHandler = () => setIsShowMenu(false)
  return (
    <StyledDropdown className={className} isInline={isInline}>
      <FormFieldLabel
        isInline={isInline}
        labelText={labelText}
        validationMessage={validationMessage}
        required={required}
      />
      <OutsideClickHandler
        display={isInline ? 'inline-block' : 'block'}
        onOutsideClick={closeHandler}
      >
        <DropdownContainer>
          <DisplayItem
            tabIndex="0"
            isActive={isShowMenu}
            isShowBorder={isShowBorder}
            isDisabled={isDisabled}
            onClick={() => {
              if (isDisabled) {
                return
              }
              setIsShowMenu(!isShowMenu)
            }}
          >
            <DisplayText>
              {displayText ? displayText : isShowDefault ? items[0].text : null}
            </DisplayText>
            <StyledDropdownCaretDownIcon />
          </DisplayItem>
          {isShowMenu && <StyledOverlay onClick={closeHandler} />}
          {isShowMenu && (
            <StyledDropdownMenu
              isInline={isInline}
              isLeft={isMenuLeft}
              isPositionDown={isMenuPositionDown}
            >
              {items.map((item) => {
                const isItemSelected = !!selectedItems.find(
                  (si) => item.value === si.value
                )
                return (
                  <DropdownItem
                    key={item.text}
                    isSelected={isItemSelected}
                    onSelect={() => {
                      if (onChange) {
                        const itemValue =
                          item.value === undefined ? item.text : item.value
                        if (isMulti) {
                          if (isItemSelected) {
                            onChange(
                              value
                                .filter((v) => itemValue !== v)
                                .filter((v) => v)
                            )
                          } else {
                            onChange([itemValue].concat(value).filter((v) => v))
                          }
                        } else {
                          onChange(itemValue)
                          setIsShowMenu(false)
                        }
                      }
                    }}
                  >
                    {item.text}
                  </DropdownItem>
                )
              })}
            </StyledDropdownMenu>
          )}
        </DropdownContainer>
      </OutsideClickHandler>
    </StyledDropdown>
  )
}

export { Dropdown }
